import { useState } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Menu, X, ArrowUpRight, Languages } from "lucide-react";
import { scrollToId } from "./common";
import { useLang, t } from "../i18n";

const LINKS = [
  { id: "focus", key: "nav_focus", num: "01" },
  { id: "publications", key: "nav_publications", num: "02" },
  { id: "journey", key: "nav_journey", num: "03" },
  { id: "chunav", key: "nav_chunav", num: "04" },
  { id: "honours", key: "nav_honours", num: "05" },
  { id: "connect", key: "nav_connect", num: "06" },
];

const Navbar = () => {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 90, damping: 24 });

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50" data-testid="site-navbar">
      <div className="backdrop-blur-xl bg-bone/80 border-b border-ink/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between gap-6">
          <button
            data-testid="nav-brand"
            onClick={() => window.__lenis ? window.__lenis.scrollTo(0, { duration: 1.4 }) : window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-3 group"
          >
            <span className="w-9 h-9 rounded-full bg-crimson text-bone font-display font-bold text-lg grid place-items-center group-hover:rotate-[15deg] transition-transform duration-300">
              AV
            </span>
            <span className="text-left leading-tight">
              <span className="block font-display font-bold text-ink text-sm sm:text-base">Dr. Abhishek Verma</span>
              <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-mutedink">{t(lang, "nav_brand_role")}</span>
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-7" data-testid="nav-links">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.num}-${l.id}`}
                onClick={() => go(l.id)}
                className="ink-underline font-mono text-[11px] uppercase tracking-[0.2em] text-ink/70 hover:text-ink transition-colors pb-1"
              >
                <span className="text-crimson mr-1.5">{l.num}</span>
                {t(lang, l.key)}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-lang-toggle"
              onClick={() => setLang(lang === "en" ? "hi" : "en")}
              className="inline-flex items-center gap-1.5 border border-ink/20 text-ink font-mono text-[11px] tracking-[0.1em] px-4 py-2.5 rounded-full hover:border-crimson/50 hover:text-crimson transition-colors"
              aria-label="Toggle language English/Hindi"
            >
              <Languages size={13} />
              {lang === "en" ? "हिंदी" : "EN"}
            </button>
            <a
              data-testid="nav-cta-bharatelect"
              href="https://electbharat.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-2 bg-ink text-bone font-mono text-[11px] uppercase tracking-[0.18em] px-5 py-2.5 rounded-full hover:bg-crimson transition-colors duration-300"
            >
              {t(lang, "nav_cta")}
              <ArrowUpRight size={14} />
            </a>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen(!open)}
              className="lg:hidden w-10 h-10 grid place-items-center text-ink"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
        <motion.div className="h-[2px] bg-crimson origin-left" style={{ scaleX }} />
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:hidden bg-bone border-b border-ink/10 px-5 py-6 space-y-1"
          data-testid="nav-mobile-menu"
        >
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-mobile-link-${l.id}`}
              onClick={() => go(l.id)}
              className="block w-full text-left py-3 font-display text-2xl text-ink border-b border-ink/10"
            >
              <span className="font-mono text-xs text-crimson mr-3">{l.num}</span>
              {t(lang, l.key)}
            </button>
          ))}
          <a
            data-testid="nav-mobile-cta-bharatelect"
            href="https://electbharat.com"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 flex items-center justify-center gap-2 bg-ink text-bone font-mono text-xs uppercase tracking-[0.18em] px-5 py-4 rounded-full"
          >
            {t(lang, "nav_cta")} — electbharat.com
            <ArrowUpRight size={14} />
          </a>
        </motion.div>
      )}
    </header>
  );
};

export default Navbar;
